"use client";

import * as React from "react";
import { Download, FileSpreadsheet, FileText, FileType } from "lucide-react";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@aio/platform-ui";

/**
 * Format identifier passed back through {@link ExportMenuProps.onSelect}.
 *
 * Caller maps this to the appropriate `to-xlsx` / `to-csv` / `to-pdf` writer
 * — keeping the menu format-agnostic means the toolbar code can swap writers
 * without changing the menu itself.
 */
export type ExportFormat = "xlsx" | "csv" | "pdf";

/** Props for {@link ExportMenu}. */
export interface ExportMenuProps {
  /** Called when the user selects a format. Caller produces the Blob and downloads it. */
  onSelect: (format: ExportFormat) => void | Promise<void>;
  /**
   * Disable individual entries with a per-format reason. Useful when PDF
   * generation isn't available in the current build (e.g. server-render
   * issues) — caller passes `pdf: "Coming soon"` and the item renders as
   * disabled with the tooltip.
   */
  disabledReason?: Partial<Record<ExportFormat, string>>;
  /** Override the trigger button's accessible label. Defaults to "Export". */
  triggerLabel?: string;
  /** Override the menu's heading label. Defaults to "Export as". */
  menuLabel?: string;
  /** Extra classes for the trigger button. */
  className?: string;
}

interface MenuItemConfig {
  format: ExportFormat;
  label: string;
  description: string;
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
}

/**
 * Static menu item registry. Adding a new format means appending here and
 * extending {@link ExportFormat}; everything else (icons, copy, tooltip
 * handling) flows through the table-driven render.
 */
const MENU_ITEMS: MenuItemConfig[] = [
  {
    format: "xlsx",
    label: "Export as XLSX",
    description: "Spreadsheet, opens in Excel / Sheets",
    Icon: FileSpreadsheet,
  },
  {
    format: "csv",
    label: "Export as CSV",
    description: "Comma-separated values",
    Icon: FileText,
  },
  {
    format: "pdf",
    label: "Export as PDF",
    description: "Print-ready document",
    Icon: FileType,
  },
];

/**
 * Render one menu row. Encapsulated in its own component so the disabled
 * branch can host a Radix Tooltip without disturbing the parent's keyboard
 * focus management — `DropdownMenuItem disabled` already swallows pointer
 * events, but the wrapper keeps the tooltip hover region intact.
 */
function ExportMenuRow({
  item,
  onSelect,
  disabledReason,
}: {
  item: MenuItemConfig;
  onSelect: ExportMenuProps["onSelect"];
  disabledReason?: string | undefined;
}): React.JSX.Element {
  const { format, label, description, Icon } = item;
  const isDisabled = Boolean(disabledReason);

  const itemNode = (
    <DropdownMenuItem
      disabled={isDisabled}
      onSelect={(event) => {
        if (isDisabled) {
          event.preventDefault();
          return;
        }
        // Radix closes on select by default — we want the close, plus we
        // dispatch the format to the caller in a microtask so the menu's
        // closing animation isn't blocked by a synchronous PDF render.
        Promise.resolve().then(() => onSelect(format));
      }}
      data-testid={`export-menu-${format}`}
      // Keep the icon + label on one row, with the description underneath.
      className={cn(
        "flex flex-col items-start gap-0.5 py-2",
        // Disabled state cue beyond Radix's data-disabled — visible as
        // strikethrough on the label so the "coming soon" intent is obvious.
        isDisabled && "opacity-60",
      )}
    >
      <div className="flex w-full items-center gap-2">
        <Icon className="size-4" aria-hidden />
        <span className={cn("text-sm font-medium", isDisabled && "line-through")}>
          {label}
        </span>
      </div>
      <span className="ml-6 text-xs text-fg-muted">{description}</span>
    </DropdownMenuItem>
  );

  if (!isDisabled) return itemNode;

  // Wrap the disabled item in a tooltip so the user understands WHY. The
  // tooltip's TooltipTrigger forwards interactions to the inner item; pointer
  // events still work because Radix only blocks the `onSelect` path.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div data-testid={`export-menu-${format}-wrap`}>{itemNode}</div>
      </TooltipTrigger>
      <TooltipContent side="left" align="center">
        {disabledReason}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Toolbar component: a Download button that opens a dropdown listing the
 * available export formats.
 *
 * Caller is responsible for converting the selected format into a Blob and
 * triggering the download. A typical wiring looks like:
 *
 * ```ts
 * <ExportMenu
 *   onSelect={async (format) => {
 *     const blob =
 *       format === "xlsx" ? await toXlsx(opts) :
 *       format === "csv"  ? toCsv(opts) :
 *                           await toPdf(opts);
 *     const url = URL.createObjectURL(blob);
 *     const a = document.createElement("a");
 *     a.href = url;
 *     a.download = `invoices.${format}`;
 *     a.click();
 *     URL.revokeObjectURL(url);
 *   }}
 * />
 * ```
 *
 * Tooltips for disabled items require a `TooltipProvider` ancestor. If your
 * app already wraps the layout in one (the platform-ui app shell does), the
 * inner provider here is harmless — Radix permits nesting.
 */
export function ExportMenu({
  onSelect,
  disabledReason,
  triggerLabel = "Export",
  menuLabel = "Export as",
  className,
}: ExportMenuProps): React.JSX.Element {
  return (
    <TooltipProvider delayDuration={150}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn("gap-1.5", className)}
            data-testid="export-menu-trigger"
            aria-label={triggerLabel}
          >
            <Download className="size-4" aria-hidden />
            <span>{triggerLabel}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {MENU_ITEMS.map((item) => (
            <ExportMenuRow
              key={item.format}
              item={item}
              onSelect={onSelect}
              disabledReason={disabledReason?.[item.format]}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
